<template>
  <div class="content sis-page">
    <!-- ============================================================
         TOOLBAR CARD: Bộ lọc tìm kiếm
         ============================================================ -->
    <div class="row-data sis-toolbar-card">
      <div class="sis-card-head">
        <div>
          <p class="sis-card-title">Tổng hợp phiếu thu theo học sinh</p>
          <p class="sis-card-subtitle">
            Tìm kiếm và theo dõi toàn bộ phiếu thu, công nợ tổng hợp của từng học sinh.
          </p>
        </div>
        <div class="sis-card-head-actions">
          <el-button
            v-if="checkPermission(['tuition_excel_export_v4'])"
            type="success"
            size="small"
            icon="el-icon-download"
            :loading="isExporting"
            :disabled="selectedStudents.length === 0"
            @click="handleExportInvoices"
          >Xuất Excel theo phiếu thu</el-button>
          <el-button
            plain
            icon="el-icon-refresh-left"
            size="small"
            class="sis-reset-btn"
            @click="handleResetFilters"
          >
            Làm mới bộ lọc
          </el-button>
        </div>
      </div>

      <el-form
        ref="searchFormRef"
        :model="filter"
        :rules="searchRules"
        label-position="top"
        class="sis-form-grid"
      >
        <!-- Trạng thái học sinh -->
        <el-form-item label="Trạng thái học sinh" class="sis-form-item">
          <el-select
            v-model="filter.kidStatus"
            placeholder="Tất cả trạng thái"
            clearable
            style="width: 170px"
            @change="handleSearch"
          >
            <el-option
              v-for="s in statusOptions"
              :key="s.key"
              :label="s.label || s.value"
              :value="s.key"
            />
          </el-select>
        </el-form-item>

        <!-- Khối -->
        <el-form-item label="Khối" prop="idGrade" class="sis-form-item">
          <el-select
            v-model="filter.idGrade"
            placeholder="Chọn khối"
            :clearable="false"
            style="width: 150px"
            @change="handleGradeChange"
          >
            <el-option
              v-for="g in gradeOfSchoolList"
              :key="g.id"
              :label="g.gradeName"
              :value="g.id"
            />
          </el-select>
        </el-form-item>

        <!-- Lớp -->
        <el-form-item label="Lớp" prop="idClass" class="sis-form-item">
          <el-select
            v-model="filter.idClass"
            placeholder="Chọn lớp"
            :clearable="false"
            style="width: 180px"
            @change="handleSearch"
          >
            <el-option
              v-for="c in filteredClasses"
              :key="c.id"
              :label="c.className"
              :value="c.id"
            />
          </el-select>
        </el-form-item>

        <!-- Tên học sinh -->
        <el-form-item label="Tìm theo tên học sinh" class="sis-form-item">
          <el-input
            v-model="filter.studentName"
            placeholder="Nhập tên học sinh..."
            clearable
            style="width: 250px"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          >
            <template #append>
              <el-button icon="el-icon-search" @click="handleSearch" />
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </div>

    <!-- ============================================================
         TABLE CARD: Danh sách tổng hợp học sinh
         ============================================================ -->
    <div class="row-data sis-table-card">
      <div class="sis-card-head sis-table-head">
        <div>
          <p class="sis-card-title">Danh sách học sinh</p>
          <p class="sis-card-subtitle">
            Hiển thị {{ studentRows.length }} / {{ totalElements }} học sinh. Bấm vào nút tác vụ để xem chi tiết phiếu thu.
          </p>
        </div>
      </div>

      <div class="sis-table-wrapper">
        <el-table
          ref="studentTableRef"
          :data="studentRows"
          class="sk-table sis-main-table"
          border
          highlight-current-row
          v-loading="loading"
          :empty-text="emptyText"
          :max-height="$tableMaxHeight"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="52" fixed="left" />
          <!-- STT -->
          <el-table-column
            type="index"
            label="STT"
            width="50"
            align="center"
            fixed="left"
          />

          <!-- Họ tên học sinh -->
          <el-table-column
            prop="studentName"
            label="Họ và tên"
            min-width="220"
            fixed="left"
            align="left"
          >
            <template slot-scope="{ row }">
              <el-tooltip :content="`Mã HS: ${row.studentCode || '—'}`" placement="top" :enterable="false">
                <span class="sis-student-name-cell">{{ row.studentName }}</span>
              </el-tooltip>
            </template>
          </el-table-column>

          <!-- Phiếu lớp -->
          <el-table-column label="Phiếu lớp" min-width="140" align="center">
            <template slot-scope="{ row }">
              <span class="sis-count-badge sis-count-badge--class">
                {{ row.summary ? row.summary.classInvoiceCount || 0 : 0 }}
              </span>
            </template>
          </el-table-column>

          <!-- Phiếu nhóm -->
          <el-table-column label="Phiếu nhóm" min-width="140" align="center">
            <template slot-scope="{ row }">
              <span class="sis-count-badge sis-count-badge--group">
                {{ row.summary ? row.summary.groupInvoiceCount || 0 : 0 }}
              </span>
            </template>
          </el-table-column>

          <!-- Phiếu gói -->
          <el-table-column label="Phiếu gói" min-width="140" align="center">
            <template slot-scope="{ row }">
              <span class="sis-count-badge sis-count-badge--package">
                {{ row.summary ? row.summary.packageInvoiceCount || 0 : 0 }}
              </span>
            </template>
          </el-table-column>

          <!-- Tổng phải thu -->
          <el-table-column
            label="Tổng phải thu"
            align="right"
            min-width="140"
            class-name="sis-finance-column"
            label-class-name="sis-finance-header"
          >
            <template slot-scope="{ row }">
              <span class="sis-money sis-money--bold">
                {{ (row.summary ? row.summary.totalMoney : 0) | formatCurrency }}
              </span>
            </template>
          </el-table-column>

          <!-- Còn lại -->
          <el-table-column
            label="Còn lại"
            align="right"
            min-width="140"
            class-name="sis-finance-column"
            label-class-name="sis-finance-header"
          >
            <template slot-scope="{ row }">
              <span
                :class="(row.summary && row.summary.remainingMoney > 0) ? 'sis-money--positive' : 'sis-money--negative'"
                class="sis-money"
              >
                {{ (row.summary ? row.summary.remainingMoney : 0) | formatCurrency }}
              </span>
            </template>
          </el-table-column>

          <!-- Tác vụ -->
          <el-table-column label="Tác vụ" width="90" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-tooltip content="Xem chi tiết phiếu thu" placement="top" :enterable="false">
                <el-button
                  size="mini"
                  type="primary"
                  icon="el-icon-tickets"
                  circle
                  plain
                  @click.stop="handleOpenDetail(row)"
                />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Phân trang -->
      <div class="sis-pagination">
        <el-pagination
          background
          :current-page="page + 1"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size"
          :total="totalElements"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- ============================================================
         DIALOG CHI TIẾT PHIẾU THU HỌC SINH
         ============================================================ -->
    <student-invoice-detail-dialog
      ref="detailDialog"
      @saved="loadSummary"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { kidStatusListExcel } from "@/constant/listCommon";
import checkPermission from "@/utils/permission";
import StudentInvoiceDetailDialog from "@/components/tutition_v3/dialog/StudentInvoiceDetailDialog.vue";
import { exportStudentInvoicesMultiSheetExcel } from "@/commonFuncion/exportStudentInvoicesMultiSheetExcel";

export default {
  name: "StudentInvoiceSummary",
  components: {
    StudentInvoiceDetailDialog,
  },

  data() {
    return {
      statusOptions: kidStatusListExcel,
      loading: false,
      isExporting: false,
      emptyText: "Không có dữ liệu học sinh phù hợp.",
      studentRows: [],
      selectedStudents: [],
      totalElements: 0,
      page: 0,
      size: 20,
      filter: {
        kidStatus: "STUDYING",
        idGrade: null,
        idClass: null,
        studentName: "",
      },
      searchRules: {
        idGrade: [{ required: true, message: "Vui lòng chọn khối", trigger: "change" }],
        idClass: [{ required: true, message: "Vui lòng chọn lớp", trigger: "change" }],
      },
    };
  },

  computed: {
    ...mapState("gradeModule", ["gradeOfSchoolList"]),
    ...mapState("classStore", ["classListCommon"]),

    filteredClasses() {
      return (this.classListCommon || []).filter(
        (c) => !this.filter.idGrade || c.idGrade === this.filter.idGrade
      );
    },

    currentClassName() {
      const found = (this.classListCommon || []).find((c) => c.id === this.filter.idClass);
      return found ? found.className : "";
    },
  },

  watch: {
    gradeOfSchoolList: {
      immediate: true,
      handler(list) {
        if (Array.isArray(list) && list.length > 0 && !this.filter.idGrade) {
          this.filter.idGrade = list[0].id;
        }
      },
    },

    filteredClasses: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list) || list.length === 0) {
          this.filter.idClass = null;
          return;
        }
        const hasCurrentClass = list.some((item) => item.id === this.filter.idClass);
        if (!hasCurrentClass) {
          this.filter.idClass = list[0].id;
          this.loadSummary();
        }
      },
    },
  },

  async created() {
    await this.$store.dispatch("gradeModule/fetchDataGradeOfSchoolList");
    await this.$store.dispatch("classStore/fetchDataClassListCommon");

    if (Array.isArray(this.gradeOfSchoolList) && this.gradeOfSchoolList.length > 0 && !this.filter.idGrade) {
      this.filter.idGrade = this.gradeOfSchoolList[0].id;
    }
    const classes = this.filteredClasses;
    if (Array.isArray(classes) && classes.length > 0 && !this.filter.idClass) {
      this.filter.idClass = classes[0].id;
    }
    this.loadSummary();
  },

  methods: {
    checkPermission,

    async loadSummary() {
      if (!this.filter.idGrade || !this.filter.idClass) {
        return;
      }
      this.loading = true;
      try {
        const params = {
          kidStatus: this.filter.kidStatus,
          idGrade: this.filter.idGrade,
          idClass: this.filter.idClass,
          studentName: this.filter.studentName ? this.filter.studentName.trim() : null,
          page: this.page,
          size: this.size,
        };

        // Loại bỏ tham số rỗng / null / undefined trước khi gửi
        Object.keys(params).forEach((k) => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) {
            delete params[k];
          }
        });

        const res = await ManagingStudentInvoiceService.filterStudentInvoiceSummary(params);
        const data = res?.data?.data || {};
        this.studentRows = Array.isArray(data.content) ? data.content : [];
        this.totalElements = data.totalElements || 0;
        this.selectedStudents = [];
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải danh sách tổng hợp học sinh!"
        );
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.page = 0;
      this.loadSummary();
    },

    handleGradeChange() {
      const classes = this.filteredClasses;
      this.filter.idClass = Array.isArray(classes) && classes.length > 0 ? classes[0].id : null;
      this.handleSearch();
    },

    handleResetFilters() {
      const defaultGrade = Array.isArray(this.gradeOfSchoolList) && this.gradeOfSchoolList.length > 0
        ? this.gradeOfSchoolList[0].id
        : null;
      const classes = (this.classListCommon || []).filter(
        (c) => !defaultGrade || c.idGrade === defaultGrade
      );
      const defaultClass = Array.isArray(classes) && classes.length > 0 ? classes[0].id : null;

      this.filter = {
        kidStatus: "STUDYING",
        idGrade: defaultGrade,
        idClass: defaultClass,
        studentName: "",
      };
      this.page = 0;
      this.loadSummary();
    },

    handlePageChange(page) {
      this.page = page - 1;
      this.loadSummary();
    },

    handleSizeChange(size) {
      this.size = size;
      this.page = 0;
      this.loadSummary();
    },

    /**
     * Mở Dialog chi tiết phiếu thu của học sinh
     */
    handleOpenDetail(row) {
      if (this.$refs.detailDialog) {
        this.$refs.detailDialog.open({
          ...row,
          className: row.className || this.currentClassName,
        });
      }
    },

    handleSelectionChange(rows) {
      this.selectedStudents = rows || [];
    },

    async handleExportInvoices() {
      if (!this.filter.idGrade || !this.filter.idClass) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn khối và lớp trước khi xuất Excel.");
        return;
      }
      this.isExporting = true;
      try {
        const payload = {
          kidStatus: this.filter.kidStatus,
          idGrade: this.filter.idGrade,
          idClass: this.filter.idClass,
          studentName: this.filter.studentName ? this.filter.studentName.trim() : null,
        };

        if (this.selectedStudents && this.selectedStudents.length > 0) {
          payload.studentIds = this.selectedStudents.map((row) => row.idStudent);
        }

        Object.keys(payload).forEach((k) => {
          if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
            delete payload[k];
          }
        });

        const response = await ManagingStudentInvoiceService.exportStudentInvoices(payload);
        const invoices = response?.data?.data || [];

        if (!Array.isArray(invoices) || invoices.length === 0) {
          NotifyMessage.leftCornerNotificationWarning("Không có dữ liệu phiếu thu để xuất Excel.");
          return;
        }

        const title = `DANH SÁCH PHIẾU THU HỌC SINH ${this.currentClassName ? `- LỚP ${this.currentClassName.toUpperCase()}` : ""}`;

        await exportStudentInvoicesMultiSheetExcel({
          invoices,
          title,
        });

        NotifyMessage.leftCornerNotificationSuccess("Xuất Excel danh sách phiếu thu học sinh thành công.");
      } catch (error) {
        console.error(error);
        NotifyMessage.leftCornerNotificationError(
          error?.response?.data?.message || "Không thể xuất Excel theo phiếu thu."
        );
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-table.scss";

/* ─── Page Container ─────────────────────────────────── */
.sis-page {
  padding: 4px 0 16px;
  .row-data {
    margin-bottom: 20px;
  }
}

/* ─── Toolbar Card ───────────────────────────────────── */
.sis-toolbar-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  padding: 18px 20px;
}

.sis-form-grid {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.sis-form-item {
  margin-bottom: 0;
  flex-shrink: 0;
}

/deep/ .sis-form-item .el-form-item__label {
  padding-bottom: 4px;
  font-size: 12.5px;
  color: #475569;
  font-weight: 600;
  line-height: 1.5;
}

.sis-reset-btn {
  background: #f1f5f9 !important;
  border: 1px solid #cbd5e1 !important;
  color: #475569 !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0 !important;
    color: #1e293b !important;
  }
}

/* ─── Card Head ──────────────────────────────────────── */
.sis-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.sis-table-head {
  align-items: center;
}

.sis-card-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.sis-card-subtitle {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #64748b;
}

/* ─── Table Card ─────────────────────────────────────── */
.sis-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  padding: 18px 18px 14px;
}

.sis-table-wrapper {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

/* ─── Table Header Primary Blue ──────────────────────── */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

/deep/ .sk-table thead tr {
  height: 54px;
}

/deep/ .sk-table tbody td {
  background: #ffffff;
}

/deep/ .sk-table.el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: #f1f5f9 !important;
  cursor: pointer;
}

/deep/ .sk-table .current-row > td {
  background-color: #e0f2fe !important;
}

/* ─── Finance Column Styling ─────────────────────────── */
/deep/ .sk-table thead th.sis-finance-header,
/deep/ .sk-table thead td.sis-finance-header {
  background: #0284c7 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}

/deep/ .sk-table thead th.sis-finance-header > .cell,
/deep/ .sk-table thead td.sis-finance-header > .cell {
  color: #ffffff !important;
  font-weight: 700 !important;
}

/deep/ .sk-table tbody td.sis-finance-column {
  background-color: #f0fdfa !important;
}

/* ─── Table Cells & Badges ───────────────────────────── */
.sis-student-name-cell {
  font-weight: 600;
  color: #1e293b;
  font-size: 13.5px;
}

.sis-count-badge {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  text-align: center;

  &--class {
    background: #e0f2fe;
    color: #0369a1;
  }
  &--group {
    background: #fef3c7;
    color: #b45309;
  }
  &--package {
    background: #dcfce7;
    color: #15803d;
  }
}

.sis-money {
  font-weight: 600;
  font-size: 13px;

  &--bold {
    font-weight: 700;
    color: #0f172a;
  }

  &--positive {
    color: #dc2626;
    font-weight: 700;
  }

  &--negative {
    color: #16a34a;
    font-weight: 700;
  }
}

/* ─── Phân trang ─────────────────────────────────────── */
.sis-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* ─── Input & Button Overrides ───────────────────────── */
/deep/ .sis-page .el-input__inner,
/deep/ .sis-page .el-select .el-input__inner {
  border-radius: 10px;
}

/deep/ .sis-page .el-button {
  border-radius: 10px;
}

/deep/ .sis-page .el-button.is-circle {
  border-radius: 50% !important;
}

/deep/ .sis-page .el-input-group__append .el-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
